import React, { useState } from 'react';
import useGameStore from '../store/gameStore';
import { EMPTY_ARRAY } from '../store/stableEmpties';
import { useTranslation } from '../i18n';
import { formatMoney } from '../utils/format';
import { INVESTMENTS } from '../data/investments';
import { DEPOT_ZONES } from '../data/depotZones';
import DepotHST from '../components/hst-depot3d/DepotHST';
import DepotNetworkMap from '../components/depot/DepotNetworkMap';
import HQOfficePanel from '../components/game/HQOfficePanel';
import LocalRecruitSection from '../components/hr/LocalRecruitSection';
import { getDepotEffectiveInvestments } from '../data/depotPhases';
import { ChevronDown, ChevronUp, Warehouse, Map, Building2, Globe2 } from 'lucide-react';

/**
 * Depot page — refonte 08/02/2026 (Q2 Phase 3 user prod).
 *
 * Structure :
 *   1. Header avec onglets de navigation principaux :
 *      • « Carte stratégique »  → DepotNetworkMap plein écran (landing).
 *      • « Dépôt X »  (un par dépôt possédé) → vue dédiée à ce dépôt.
 *      • « Siège social »  → HQOfficePanel (sous-onglet dédié).
 *   2. Tableau stratégie multi-dépôt (DepotStrategyPanel) RETIRÉ.
 *   3. Score productivité présenté en accordéon (détail masqué par défaut).
 *
 * Auparavant tout était empilé en un long scroll : carte + tabs + stats +
 * HUD + scene + tableau stratégie + siège. Désormais, navigation tabbée.
 */
export default function Depot() {
  const { activeInvestments, vehicles } = useGameStore();
  const ownedDepots = useGameStore(s => s.ownedDepots || EMPTY_ARRAY);
  const switchActiveDepot = useGameStore(s => s.switchActiveDepot);
  const { t } = useTranslation();

  // 08/02/2026 — Section onglets : 'map' (landing), <depotId>, 'hq-office'.
  const [view, setView] = useState('map');
  const activeDepot = ownedDepots.find(d => d.id === view) || null;
  const isMapView = view === 'map';
  const isHqOffice = view === 'hq-office';
  // 28/07/2026 — Onglet Recrutement local (déplacé depuis RH, demande user).
  const isLocalRecruit = view === 'local-recruit';

  // 08/02/2026 — Edge case (testing_agent iter-77) : si le joueur perd le
  // dépôt actif (vente, faillite, rollback save), on retombe sur la carte
  // pour éviter une page fantôme vide.
  React.useEffect(() => {
    if (!isMapView && !isHqOffice && !isLocalRecruit && !activeDepot) {
      setView('map');
    }
  }, [isMapView, isHqOffice, isLocalRecruit, activeDepot]);

  // 08/02/2026 — Écoute l'évènement émis par DepotNetworkMap (« Gérer ce
  // dépôt ») pour basculer automatiquement vers l'onglet du dépôt cible.
  React.useEffect(() => {
    function onSwitch(e) {
      const depotId = e?.detail?.depotId;
      if (depotId) setView(depotId);
    }
    document.addEventListener('hst-switch-depot-view', onSwitch);
    return () => document.removeEventListener('hst-switch-depot-view', onSwitch);
  }, []);

  return (
    <div className="space-y-4" data-testid="depot-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h2 className="text-2xl font-bold text-foreground">{t('depot_title')}</h2>
      </div>

      {/* 08/02/2026 — Nav onglets principale : Carte / Dépôts / Siège social.
          L'ancien anchor `id="depot-management"` (avant refonte) est conservé
          ici pour ne pas casser les CTA « Gérer ce dépôt » dans la map qui
          font un scrollIntoView. */}
      <div id="depot-management" className="scroll-mt-4">
        <DepotNavTabs
          view={view}
          setView={(next) => {
            setView(next);
            if (next !== 'map' && next !== 'hq-office') {
              switchActiveDepot(next);
            }
          }}
          depots={ownedDepots}
        />
      </div>

      {/* === VIEW: Carte stratégique (landing) ============================== */}
      {isMapView && (
        <div data-testid="depot-view-map">
          <DepotNetworkMap />
        </div>
      )}

      {/* === VIEW: Siège social ============================================ */}
      {isHqOffice && (
        <div data-testid="depot-view-hq-office">
          <HQOfficePanel />
        </div>
      )}

      {/* === VIEW: Recrutement local (28/07/2026) =========================== */}
      {isLocalRecruit && (
        <div data-testid="depot-view-local-recruit">
          <LocalRecruitSection />
        </div>
      )}

      {/* === VIEW: Dépôt précis ============================================ */}
      {activeDepot && (
        <DepotDetailView
          depot={activeDepot}
          activeInvestments={activeInvestments}
          vehicles={vehicles}
          t={t}
        />
      )}
    </div>
  );
}

/** Sub-component : nav tabs (Carte / Dépôt 1..N / Siège social). */
function DepotNavTabs({ view, setView, depots }) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap border-b border-ink-600 pb-2" data-testid="depot-nav-tabs">
      <DepotNavTab
        icon={<Map className="w-3.5 h-3.5" />}
        label={/* i18n-keep */ 'Carte stratégique'}
        active={view === 'map'}
        onClick={() => setView('map')}
        testId="depot-nav-map"
      />
      {depots.map(d => (
        <DepotNavTab
          key={d.id}
          icon={<Building2 className="w-3.5 h-3.5" />}
          label={d.name || (d.isHeadquarters ? 'QG' : /* i18n-keep */ 'Dépôt')}
          active={view === d.id}
          onClick={() => setView(d.id)}
          testId={`depot-nav-${d.id}`}
        />
      ))}
      <DepotNavTab
        icon={<Warehouse className="w-3.5 h-3.5" />}
        label={/* i18n-keep */ 'Siège social'}
        active={view === 'hq-office'}
        onClick={() => setView('hq-office')}
        testId="depot-nav-hq-office"
      />
      <DepotNavTab
        icon={<Globe2 className="w-3.5 h-3.5" />}
        label={/* i18n-keep */ 'Recrutement local'}
        active={view === 'local-recruit'}
        onClick={() => setView('local-recruit')}
        testId="depot-nav-local-recruit"
      />
    </div>
  );
}

function DepotNavTab({ icon, label, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all border ${
        active
          ? 'bg-amber-500/20 border-amber-400 text-amber-100 shadow-md'
          : 'border-transparent text-slate-400 hover:text-slate-200 hover:bg-ink-700/40'
      }`}
      data-testid={testId}
      aria-pressed={active}
    >
      {icon}
      <span className="max-w-[160px] truncate">{label}</span>
    </button>
  );
}

/** Vue dédiée à UN dépôt précis (image, stats, productivité, upgrades). */
function DepotDetailView({ depot, activeInvestments, vehicles, t }) {
  const isHQ = depot.isHeadquarters;

  // 13/07/2026 — Progression unifiée : le QG lit les investissements
  // globaux ; un dépôt secondaire lit SES investissements (unifiés +
  // anciennes évolutions legacy mappées). Toute la vue (stats, scène,
  // productivité, arbre) est pilotée par cette source.
  const depotInv = isHQ ? activeInvestments : getDepotEffectiveInvestments(depot);

  // Stats spécifiques au dépôt courant.
  const depotVehicles = vehicles.filter(v => (v.depotId || 'depot-hq') === depot.id);
  const idleVehicles = depotVehicles.filter(v => v.status === 'idle').length;
  const activeVehicles = depotVehicles.filter(v => v.status === 'active').length;

  const totalZones = DEPOT_ZONES.length;
  const activeZones = DEPOT_ZONES.filter(z => z.baseActive || (z.investmentId && depotInv[z.investmentId])).length;
  const totalMonthlyCost = DEPOT_ZONES.reduce((sum, z) => {
    const mainInv = z.investmentId ? INVESTMENTS.find(i => i.id === z.investmentId) : null;
    let cost = mainInv && depotInv[z.investmentId] ? (mainInv.chargeMonthly || 0) : 0;
    z.upgrades.forEach(u => {
      const inv = INVESTMENTS.find(i => i.id === u.investmentId);
      if (inv && depotInv[u.investmentId]) cost += inv.chargeMonthly || 0;
    });
    return sum + cost;
  }, 0);

  // Productivity score : score uniquement (détail en accordéon).
  const depotEnergyInv = INVESTMENTS.filter(i => i.category === 'Dépôt' || i.category === 'Énergie'); // i18n-keep
  const activeBonus = depotEnergyInv.filter(i => depotInv[i.id]).reduce((s, i) => s + Math.abs(i.impactMultiplier || 0), 0);
  const maxBonus = depotEnergyInv.reduce((s, i) => s + Math.abs(i.impactMultiplier || 0), 0);
  const productivityPct = maxBonus > 0 ? Math.round((activeBonus / maxBonus) * 100) : 0;
  const activeBonuses = depotEnergyInv.filter(i => depotInv[i.id]);

  return (
    <div className="space-y-4" data-testid={`depot-view-${depot.id}`}>
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Stat label={t('active_zones')} value={`${activeZones}/${totalZones}`} tone="emerald" />
        <Stat label={t('parked_vehicles')} value={idleVehicles} tone="slate" />
        <Stat label={t('vehicles_on_mission')} value={activeVehicles} tone="emerald" />
        <Stat label={t('depot_charges')} value={`${formatMoney(totalMonthlyCost)}/m`} tone="rose" />
      </div>

      {/* Productivity HUD (accordéon — détail masqué par défaut) */}
      <ProductivityHUDAccordion
        productivityPct={productivityPct}
        activeBonus={activeBonus}
        maxBonus={maxBonus}
        activeBonuses={activeBonuses}
        t={t}
      />

      {/* Visual Depot Overview — 30/09/2026 : vue 3D + frise + arbre (hst-depot3d) */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl p-3 shadow-2xl border border-slate-700 space-y-2" data-testid="depot-visual">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-2">
            <Warehouse className="w-4 h-4 text-warn" />
            <h3 className="text-foreground font-bold text-sm tracking-wide">{t('aerial_view_site')}</h3>
          </div>
        </div>
        <DepotHST depotId={depot.id} />
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const colors = {
    emerald: 'text-money',
    rose:    'text-danger',
    slate:   'text-foreground',
  };
  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 p-3 text-center">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`text-xl font-bold ${colors[tone] || colors.slate}`}>{value}</p>
    </div>
  );
}

function ProductivityHUDAccordion({ productivityPct, activeBonus, maxBonus, activeBonuses, t }) {
  // 08/02/2026 — Q2 Phase 3 user prod : par défaut on n'affiche QUE le score
  // global. Cliquer le chevron déplie la liste des bonus actifs (option 3a).
  const [open, setOpen] = React.useState(false);
  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 p-4" data-testid="depot-productivity-hud">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between"
        data-testid="depot-productivity-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-emerald-900/30">
            <span className="text-emerald-400 text-base font-bold">⚙</span>
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-slate-200">{t('depot_productivity_title')}</h3>
            <p className="text-[10px] text-slate-500">{t('depot_productivity_subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-2xl font-black font-mono text-emerald-400" data-testid="depot-productivity-pct">{productivityPct}%</p>
          {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      <div className="h-2 rounded-full bg-ink-700/40 overflow-hidden mt-2">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-amber-400 transition-all duration-700"
          style={{ width: `${productivityPct}%` }}
        />
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-ink-700/60" data-testid="depot-productivity-detail">
          <p className="text-[10px] text-slate-500 font-mono mb-2">
            +{Math.round(activeBonus * 100)}% / +{Math.round(maxBonus * 100)}% max
          </p>
          {activeBonuses.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {activeBonuses.map(b => (
                <span
                  key={b.id}
                  className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${b.impactMultiplier >= 0 ? 'bg-money/15 text-money' : 'bg-sky-500/15 text-sky-300'}`}
                  title={b.name}
                  data-testid={`depot-bonus-${b.id}`}
                >
                  {b.name} <strong>{b.impactRevenue}</strong>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-[10px] text-slate-500 italic">{t('depot_no_bonus_yet')}</p>
          )}
        </div>
      )}
    </div>
  );
}
